import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { C, MONTHS, METRIC_LABELS } from '../../utils/plantConstants';
import { dailySeries, baseLabel } from '../../utils/plantModel';

// Format a metric value for tooltips.
function fmtVal(metric, v) {
	if (metric === 'light') return `${Math.round(v).toLocaleString()} lux·h`;
	if (metric === 'nutrition') return `${Math.round(v).toLocaleString()} µS/cm`;
	if (metric === 'temperature') return `${v.toFixed(1)}°C`;
	return `${v.toFixed(0)}%`;
}

// Format an axis tick (k-formatted for light & nutrition).
function fmtAxis(metric, v) {
	if (metric === 'light' || metric === 'nutrition') return v >= 1000 ? `${Math.round(v / 100) / 10}k` : `${Math.round(v)}`;
	return `${Math.round(v)}`;
}

// Approximate calendar date label for a day-of-year.
function dateLabel(doy) {
	let m = 0;
	for (let i = 0; i < C.month_doy.length; i++) {
		if (C.month_doy[i] <= doy) m = i;
	}
	const day = doy - C.month_doy[m] + 1;
	return `${MONTHS[m]} ${day}`;
}

// action is an optional node rendered in the card header, for per-metric
// controls that belong next to the graph they act on.
const BandChart = ({ metric, params, action }) => {
	const style = C.plot_style[metric];

	const option = useMemo(() => {
		const s = dailySeries(metric, params);
		const x = s.days;

		const tolBase = s.target_min;
		const tolBand = s.target_max.map((v, i) => v - s.target_min[i]);
		const optBase = s.optimal_min;
		const optBand = s.optimal_max.map((v, i) => v - s.optimal_min[i]);

		const monthStart = new Set(C.month_doy.map((d) => d - 1));
		const monthLabel = {};
		C.month_doy.forEach((d, i) => {
			monthLabel[d - 1] = MONTHS[i];
		});

		const invisibleArea = { color: 'transparent' };

		return {
			grid: { left: 52, right: 16, top: 16, bottom: 28 },
			animation: false,
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'line', lineStyle: { type: 'dashed', color: '#9aa0a6' } },
				backgroundColor: 'rgba(43,48,54,0.96)',
				borderWidth: 0,
				textStyle: { color: '#fff', fontSize: 11 },
				formatter: (pts) => {
					const i = pts[0].dataIndex;
					return `<div style="font-family:ui-monospace,monospace">${dateLabel(x[i])}<br/>optimal ${fmtVal(metric, s.optimal_min[i])} – ${fmtVal(metric, s.optimal_max[i])}<br/>center ${fmtVal(metric, s.center[i])}</div>`;
				},
			},
			xAxis: {
				type: 'category',
				data: x,
				boundaryGap: false,
				axisTick: { show: false },
				axisLine: { lineStyle: { color: '#e2e6ea' } },
				axisLabel: {
					color: '#aeb4ba',
					fontFamily: 'ui-monospace, monospace',
					fontSize: 10,
					interval: (index) => monthStart.has(index),
					formatter: (value, index) => monthLabel[index] || '',
				},
				splitLine: { show: true, interval: (index) => monthStart.has(index), lineStyle: { color: '#5b6470', opacity: 0.12, type: [2, 3] } },
			},
			yAxis: {
				type: 'value',
				scale: true,
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: { color: '#aeb4ba', fontFamily: 'ui-monospace, monospace', fontSize: 10, formatter: (v) => fmtAxis(metric, v) },
				splitLine: { lineStyle: { color: '#5b6470', opacity: 0.12, type: [2, 3] } },
			},
			series: [
				// Tolerated band (target_min..target_max)
				{ name: 'tol_base', type: 'line', stack: 'tol', data: tolBase, symbol: 'none', lineStyle: { width: 0 }, areaStyle: invisibleArea, silent: true, z: 1 },
				{ name: 'tolerated', type: 'line', stack: 'tol', data: tolBand, symbol: 'none', lineStyle: { width: 0 }, areaStyle: { color: style.tol, opacity: 0.28 }, silent: true, z: 1 },
				// Optimal band (optimal_min..optimal_max), drawn on top
				{ name: 'opt_base', type: 'line', stack: 'opt', data: optBase, symbol: 'none', lineStyle: { width: 0 }, areaStyle: invisibleArea, silent: true, z: 2 },
				{ name: 'optimal', type: 'line', stack: 'opt', data: optBand, symbol: 'none', lineStyle: { width: 0 }, areaStyle: { color: style.opt, opacity: 0.45 }, silent: true, z: 2 },
				// Center line
				{ name: 'center', type: 'line', data: s.center, symbol: 'none', lineStyle: { width: 2, color: style.line }, z: 3 },
			],
		};
	}, [metric, params, style]);

	const subLabel = `base '${baseLabel(metric, params.base_levels[metric]).toLowerCase()}' · ${style.unit}`;

	return (
		<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
			<div className="flex items-center gap-2 px-4 pt-3 pb-1 flex-wrap">
				<span className="w-2.5 h-2.5 rounded" style={{ background: style.opt }} />
				<span className="font-semibold text-text-strong">{METRIC_LABELS[metric]}</span>
				<span className="text-xs text-text-weak font-mono ml-1">{subLabel}</span>
				<div className="ml-auto flex items-center gap-3 text-[10px] text-text-weak font-mono">
					<span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ background: style.tol, opacity: 0.5 }} />tolerated</span>
					<span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ background: style.opt, opacity: 0.7 }} />optimal</span>
				</div>
				{action}
			</div>
			<ReactECharts option={option} style={{ height: 220, width: '100%' }} notMerge={true} />
		</div>
	);
};

export default BandChart;
